/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { DateTime } from "./datetime";
import { Time } from "./time";
import { translateString } from "./translation";

export const formatDateTime = ({
  value = new Date(),
  format,
  timeDiff = 0,
  type = formatDateTime.TYPE.LONG,
  custom,
  config,
  translations,
  translator,
}) => {
  var val = null;

  if (value instanceof Date) {
    val = new DateTime(value, translator);
  } else if (typeof value == "string") {
    val = DateTime.parseMysqlDate(value);
  }
  if (!val) {
    return "";
  }
  val.setTime(val.getTime() + timeDiff * 60 * 1000);

  const dateFormat = custom || config.date_format || "Y-m-d";
  val.setDateFormat(dateFormat);
  val.setTimeFormat(config.time_format || "24hr");

  switch (format) {
    case formatDateTime.FORMAT.DAY:
      return val.getDate();
    case formatDateTime.FORMAT.DATE:
      return val.getFormattedDate(true);
    case formatDateTime.FORMAT.DATETIME:
      return val.getFormattedDateTime(true);
    case formatDateTime.FORMAT.MONTH:
      return translateString("_" + val.getMonthName(type) + "_", translations);
    case formatDateTime.FORMAT.CUSTOM:
      return val.formatDate(custom, true);
    case formatDateTime.FORMAT.TIME:
      return val.getFormattedTime();
    case formatDateTime.FORMAT.YEAR:
      return val.getFullYear();
    default:
      throw "Bad params for FormatDateTime";
  }
};

formatDateTime.FORMAT = {
  DAY: "day",
  DATE: "date",
  DATETIME: "datetime",
  MONTH: "month",
  CUSTOM: "custom",
  TIME: "time",
  YEAR: "year",
};

formatDateTime.TYPE = {
  SHORT: "short",
  LONG: "long",
};

export const formatTime = ({ value, format = formatTime.FORMAT.TIME, ampm, config }) => {
  const timeFormat = config.time_format || "24hr";

  var time = new Time(value);

  switch (format) {
    case formatTime.FORMAT.TIME:
      return time.getFormattedTime(ampm || timeFormat);

    case formatTime.FORMAT.HOURS:
      switch (ampm || timeFormat) {
        case "24hr":
          return time.getFormattedTime("hours_24hr");

        case "ampm":
          return time.getFormattedTime("hours_ampm");

        default:
      }
      break;

    default:
      throw "Bad params for FormatTime";
  }
};

formatTime.FORMAT = {
  TIME: "time",
  HOURS: "hours",
};

export const formatDatePeriod = ({
  from,
  to,
  format,
  type = formatDatePeriod.TYPE.LONG,
  custom_format,
  mergeDate,
  config,
  translator,
}) => {
  const dateFormat = custom_format || config.date_format || "Y-m-d";
  const timeFormat = config.time_format || "24hr";

  if (!from && !to) return "";

  if (!from || !to) {
    throw 'Options "from" and "to" are required';
  }
  if (typeof from == "string") {
    from = DateTime.parseMysqlDate(from);
  } else {
    from = new DateTime(from, translator);
  }
  from.setDateFormat(dateFormat);
  from.setTimeFormat(timeFormat);

  if (typeof to == "string") {
    to = DateTime.parseMysqlDate(to);
  } else {
    to = new DateTime(to, translator);
  }
  to.setDateFormat(dateFormat);
  to.setTimeFormat(timeFormat);

  var fromStr, toStr;

  switch (format) {
    case formatDatePeriod.FORMAT.DAY:
      fromStr = from.getDate();
      if (mergeDate && from.getMysqlDate() === to.getMysqlDate()) {
        toStr = false;
      } else {
        toStr = to.getDate();
      }
      break;

    case formatDatePeriod.FORMAT.DATE:
      fromStr = from.getFormattedDate(true);
      if (mergeDate && from.getMysqlDate() === to.getMysqlDate()) {
        toStr = false;
      } else {
        toStr = to.getFormattedDate(true);
      }
      break;

    case formatDatePeriod.FORMAT.DATETIME:
      fromStr = from.getFormattedDateTime(true);
      if (mergeDate && from.getMysqlDate() === to.getMysqlDate()) {
        var toTime = new Time(to);
        toStr = toTime.getFormattedTime(timeFormat);
      } else {
        toStr = to.getFormattedDateTime(true);
      }
      break;

    case formatDatePeriod.FORMAT.MONTH:
      fromStr = from.getMonthName(type);
      toStr = to.getMonthName(type);
      if (mergeDate && fromStr === toStr) {
        toStr = false;
      }
      break;

    case formatDatePeriod.FORMAT.CUSTOM:
      fromStr = from.formatDate(custom_format, true);
      toStr = to.formatDate(custom_format, true);
      break;

    case formatDatePeriod.FORMAT.TIME:
      var fromTime = new Time(from);
      var toTime = new Time(to);
      fromStr = fromTime.getFormattedTime(timeFormat);
      toStr = toTime.getFormattedTime(timeFormat);
      break;

    default:
      fromStr = from.formatDate("j", true);
      if (from.getMonth() !== to.getMonth()) {
        fromStr = from.formatDate("j F", true);
      }
      toStr = to.formatDate("j F", true);
      break;
  }

  return fromStr + (Boolean(toStr) ? " — " + toStr : "");
};

formatDatePeriod.FORMAT = {
  DAY: "day",
  DATE: "date",
  DATETIME: "datetime",
  MONTH: "month",
  CUSTOM: "custom",
  TIME: "time",
  DEFAULT: "",
};

formatDatePeriod.TYPE = {
  SHORT: "short",
  LONG: "long",
};

const currencyFormat = (amount, currency, options, translations) => {
  if (currency == "BONUS") {
    return (amount || 0) + " " + translateString("points", translations);
  }

  if (currency == "%") {
    return (amount || 0) + "%";
  }

  let formatter = new Intl.NumberFormat(undefined, {
    currency: currency,
    style: "currency",
    ...options,
  });
  return formatter.format(amount);
};

export const formatPrice = ({ value, currency, translations }) => currencyFormat(value, currency, {}, translations);

export const formatDuration = ({ value, type, translations }) => {
  switch (type) {
    case formatDuration.TYPE.YEAR:
    case formatDuration.TYPE.MONTH:
    case formatDuration.TYPE.WEEK:
    case formatDuration.TYPE.DAY:
      if (value == 1) {
        return value + " " + translateString(type, translations);
      }
      const lastDigit = Math.abs(value % 10);
      const preLastDigit = Math.abs(Math.round(value / 10) % 10);
      if (lastDigit === 1 && preLastDigit !== 1) {
        return value + " " + translateString("x1_" + type + "s", translations);
      }
      if (lastDigit > 1 && lastDigit < 5 && preLastDigit !== 1) {
        return value + " " + translateString("2_3_4_" + type + "s", translations);
      }
      return value + " " + translateString(type + "s", translations);
    case formatDuration.TYPE.HOUR:
    case formatDuration.TYPE.MINUTE:
      let result = "";
      const isNegative = value < 0;

      if (isNegative) {
        value = -value;
      }

      const hours = type == formatDuration.TYPE.MINUTE ? 0 : Math.floor(value / 60);
      const minutes = value - hours * 60;

      if (hours) {
        result += hours + " " + translateString("hr.", translations);
      }
      if (minutes) {
        if (result.length) {
          result += " ";
        }
        result += minutes + " " + translateString("mins.", translations);
      }

      if (isNegative) {
        result = "-" + result;
      }

      if (result == "") {
        result = 0 + " " + translateString("mins.", translations);
      }

      return result;
    default:
      throw "Bad params for formatDuration";
  }
};

formatDuration.TYPE = {
  YEAR: "year",
  MONTH: "month",
  WEEK: "week",
  DAY: "day",
  HOUR: "hour",
  MINUTE: "monute",
};
